import React from "react";
import { AlertTriangle, CheckCircle2, EyeOff, Info, Save, Upload, X } from "lucide-react";
import Button from "./ui/Button";
import ScrollableContainer from "./ui/ScrollableContainer";
import Tooltip from "./ui/Tooltip";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { useFileStore } from "../stores/fileStore";
import { getProjectSaveActions } from "../stores/projectSaveStore";
import { SCENE_REPLACEMENT_CONFIRM_EVENT } from "../utils/sceneReplacementGuards";
import { requestProjectSave } from "../utils/projectSaveRequests";

const ACCEPTED_DEPENDENCY_FILES =
  ".xml,.urdf,.xacro,.obj,.stl,.dae,.glb,.gltf,.mtl,.png,.jpg,.jpeg,.bmp,.tga,.dat,.bin,.npy";
const REGISTERED_ASSET_LABEL_SUFFIX = " (detected in asset store)";
const AMBIGUOUS_GEOM_ROLE_WARNING_PREFIX = "Ambiguous mesh geom role:";
const AMBIGUOUS_RAW_MESH_ROLE_WARNING_PREFIX = "Ambiguous raw mesh role:";
const RAW_MESH_UNIT_WARNING_PREFIX = "Raw mesh unit scale:";
const UPLOAD_ISSUE_FLOW_PLAN_EVENT = "critter-upload-issue-flow-plan";

const ISSUE_PRIORITY = {
  importLimit: 10,
  sceneReplacement: 20,
  missingDependencies: 30,
  mjcfWarnings: 40,
  projectIntegrity: 50,
};

const ISSUE_LABELS = {
  importLimit: "File limit",
  sceneReplacement: "Unsaved scene",
  missingDependencies: "Missing files",
  mjcfWarnings: "Import choices",
  projectIntegrity: "Integrity check",
};

const roleButtonClass = (active) =>
  `px-2 py-1 text-[11px] leading-4 rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-500 ${
    active ? "bg-gray-600 text-gray-100" : "text-gray-400 hover:bg-gray-700/60 hover:text-gray-200"
  }`;

const isMeshLimitIssue = (issue) => String(issue?.code || "").includes("mesh");
const isSizeLimitIssue = (issue) =>
  issue?.actualBytes != null ||
  issue?.limitBytes != null ||
  Boolean(issue?.limitSizeLabel || issue?.actualSizeLabel);

const parseAmbiguousRoleWarning = (warning) => {
  const text = String(warning || "");
  const meshOnlyMatch = text.match(/mesh "([^"]+)"/);
  const match = text.match(/body "([^"]+)", geom "([^"]+)", mesh "([^"]+)"/);
  if (!match && meshOnlyMatch) {
    return {
      bodyName: "Raw mesh upload",
      geomName: meshOnlyMatch[1],
      meshName: meshOnlyMatch[1],
    };
  }
  return {
    bodyName: match?.[1] || "body",
    geomName: match?.[2] || "geom",
    meshName: match?.[3] || "mesh",
  };
};

const groupMissingFiles = (missingFiles) => {
  const groups = {
    include: [],
    mesh: [],
    texture: [],
    hfield: [],
    xacroInclude: [],
    other: [],
  };

  missingFiles.forEach((entry) => {
    const text = String(entry || "")
      .trim()
      .replace(REGISTERED_ASSET_LABEL_SUFFIX, "");
    if (!text) return;
    const xacroIncludeMatch = text.match(/^xacro include:\s*(.+)$/i);
    if (xacroIncludeMatch) {
      groups.xacroInclude.push(xacroIncludeMatch[1]);
      return;
    }
    const match = text.match(/^(mesh|texture|hfield):\s*(.+)$/i);
    if (match) {
      groups[match[1].toLowerCase()].push(match[2]);
      return;
    }
    groups.include.push(text);
  });

  return groups;
};

const MissingGroup = ({ title, files }) => {
  if (!files.length) return null;
  return (
    <div className="space-y-1">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">{title}</div>
      <div className="space-y-1">
        {files.map((file) => (
          <div
            key={file}
            className="rounded border border-[rgba(95,95,95,0.4)] bg-[rgba(48,48,48,0.92)] px-2 py-1 text-xs text-neutral-200"
          >
            {file}
          </div>
        ))}
      </div>
    </div>
  );
};

const LimitStat = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="rounded border border-[rgba(95,95,95,0.4)] bg-[rgba(48,48,48,0.92)] px-2 py-1.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</div>
      <Tooltip content={value} fullWidth>
        <div className="mt-0.5 truncate text-sm text-neutral-100">{value}</div>
      </Tooltip>
    </div>
  );
};

function FlowProgressHeader({ issue }) {
  const current = issue.index + 1;
  const showProgress = issue.total > 1;
  const progressPercent = `${Math.min(100, Math.max(0, (current / issue.total) * 100))}%`;

  return (
    <div className="border-b border-gray-700/80 px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            Upload review
          </div>
          <div className="mt-0.5 truncate text-sm font-medium text-gray-100">
            {showProgress ? `Issue ${current} of ${issue.total}: ` : ""}
            {ISSUE_LABELS[issue.type] || "Upload issue"}
          </div>
        </div>
        {showProgress ? (
          <div
            className="shrink-0 rounded border border-gray-600 bg-black/20 px-2 py-1 text-xs font-medium text-gray-300"
            aria-hidden="true"
          >
            {current}/{issue.total}
          </div>
        ) : null}
      </div>
      {showProgress ? (
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-800">
          <div
            className="h-full rounded-full bg-sky-400 transition-[width] duration-300 ease-out"
            style={{ width: progressPercent }}
          />
        </div>
      ) : null}
    </div>
  );
}

function IssueHeader({ issue: _issue, title, description, tone = "warning", onClose, closeLabel }) {
  return (
    <div className="mb-3 flex items-start gap-3">
      <div
        className={
          tone === "info"
            ? "mt-0.5 rounded-md border border-sky-400/60 bg-sky-500/10 p-2 text-sky-300 shadow-[0_0_0_1px_rgba(56,189,248,0.12)]"
            : "mt-0.5 rounded-md border border-amber-400/80 bg-amber-500/10 p-2 text-amber-400 shadow-[0_0_0_1px_rgba(251,191,36,0.12)]"
        }
      >
        {tone === "info" ? (
          <Info className="h-5 w-5" aria-hidden />
        ) : (
          <AlertTriangle className="h-5 w-5" aria-hidden />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <h2 id="upload-issue-flow-title" className="text-base font-semibold text-gray-50">
          {title}
        </h2>
        {description ? <p className="mt-1 text-sm text-gray-300">{description}</p> : null}
      </div>
      {onClose ? (
        <Button
          variant="ghost"
          size="small"
          className="shrink-0 px-2 py-1 text-xs text-gray-300 hover:text-gray-100"
          onClick={onClose}
          title={closeLabel || "Close"}
          aria-label={closeLabel || "Close"}
        >
          <X className="h-4 w-4" aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}

function ImportLimitIssue({ issue, onClose }) {
  const sizeIssue = isSizeLimitIssue(issue.detail);
  const title = sizeIssue ? "Maximum Asset Size Exceeded" : "Project Archive Could Not Be Imported";
  const fileName = issue.detail.fileName || "Selected file";
  const actualSize = issue.detail.actualSizeLabel || "";
  const rejectedAssets =
    Array.isArray(issue.detail.rejectedAssets) && issue.detail.rejectedAssets.length > 0
      ? issue.detail.rejectedAssets
      : [{ fileName, actualSizeLabel: actualSize }];
  const limitSize = issue.detail.limitSizeLabel
    ? `${issue.detail.limitSizeLabel}${isMeshLimitIssue(issue.detail) ? " per mesh" : ""}`
    : "";

  const handleClose = () => {
    issue.detail.onCancelImport?.();
    onClose();
  };
  const handleProceedWithoutRejectedAsset = () => {
    issue.detail.onProceedWithoutRejectedAsset?.();
    onClose();
  };

  return (
    <>
      <IssueHeader
        issue={issue}
        title={title}
        description={
          sizeIssue
            ? "The assets listed below exceed the maximum size limit."
            : issue.detail.message || "The selected archive failed browser import validation."
        }
        onClose={handleClose}
        closeLabel="Cancel upload"
      />
      <div className="space-y-2">
        {sizeIssue ? <LimitStat label="Browser limit" value={limitSize} /> : null}
        <div className="rounded-md border border-gray-600/50 bg-black/20 p-2">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
            {sizeIssue ? "Rejected assets" : "Archive"}
          </div>
          <div className="space-y-1">
            {rejectedAssets.map((asset, index) => (
              <div
                key={`${asset.fileName}-${index}`}
                className="rounded border border-[rgba(95,95,95,0.4)] bg-[rgba(48,48,48,0.92)] px-2 py-1.5"
              >
                <Tooltip content={asset.fileName} fullWidth>
                  <div className="truncate text-xs font-medium text-neutral-100">
                    {asset.fileName}
                  </div>
                </Tooltip>
                {asset.actualSizeLabel ? (
                  <div className="mt-0.5 text-[11px] text-gray-400">{asset.actualSizeLabel}</div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-3 flex gap-2 rounded-md border border-gray-600/50 bg-black/20 p-3 text-sm text-gray-300">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden />
        {sizeIssue
          ? "Reduce the file size or split large assets into smaller files before importing."
          : "Use a .critter or .critterrefs file exported by Critter, or ask the file creator to rebuild the archive with safe relative paths."}
      </div>
      {issue.detail.canProceedWithoutRejectedAsset ? (
        <Button className="mt-4 w-full gap-2" onClick={handleProceedWithoutRejectedAsset}>
          {rejectedAssets.length > 1
            ? "Proceed Without Rejected Meshes"
            : "Proceed Without Rejected Mesh"}
        </Button>
      ) : (
        <Button className="mt-4 w-full gap-2" onClick={handleClose}>
          Close
        </Button>
      )}
    </>
  );
}

function SceneReplacementIssue({ issue, saving, saveError, onSave, onFinish }) {
  return (
    <>
      <IssueHeader
        issue={issue}
        title={issue.detail.title}
        description={issue.detail.message}
        onClose={() => onFinish(false)}
        closeLabel={issue.detail.cancelLabel}
      />
      {saveError ? (
        <div className="mt-3 rounded-md border border-red-500/60 bg-red-950/35 p-2 text-sm text-red-200">
          {saveError}
        </div>
      ) : null}
      <div className="mt-4 grid gap-2 sm:grid-cols-3">
        <Button variant="secondary" className="w-full gap-2" disabled={saving} onClick={onSave}>
          {saving ? "Saving..." : "Save"}
        </Button>
        <Button
          variant="danger"
          className="w-full gap-2 border border-red-500/70 bg-red-600/90 text-white hover:border-red-400 hover:bg-red-500"
          disabled={saving}
          onClick={() => onFinish(true)}
        >
          {issue.detail.confirmLabel}
        </Button>
        <Button
          variant="secondary"
          className="w-full gap-2"
          disabled={saving}
          onClick={() => onFinish(false)}
        >
          {issue.detail.cancelLabel}
        </Button>
      </div>
    </>
  );
}

function MissingDependenciesIssue({ issue }) {
  const inputRef = React.useRef(null);
  const missingFiles = issue.detail.missingFiles;
  const uploadingFiles = issue.detail.uploadingFiles;
  const actions = useFileStore.getState().actions;
  const groups = groupMissingFiles(missingFiles);
  const registeredMeshEntries = missingFiles.filter((entry) => {
    const text = String(entry || "").trim();
    return text.toLowerCase().startsWith("mesh:") && text.endsWith(REGISTERED_ASSET_LABEL_SUFFIX);
  });
  const hasRegisteredMeshes = registeredMeshEntries.length > 0;
  const canUseRegisteredMeshes = missingFiles.every((entry) => {
    const text = String(entry || "").trim();
    return text.toLowerCase().startsWith("mesh:") && text.endsWith(REGISTERED_ASSET_LABEL_SUFFIX);
  });
  const canProceedWithoutMissingAssets = missingFiles.every((entry) => {
    const text = String(entry || "")
      .trim()
      .replace(REGISTERED_ASSET_LABEL_SUFFIX, "")
      .toLowerCase();
    return (
      text.startsWith("mesh:") ||
      text.startsWith("texture:") ||
      text.startsWith("hfield:") ||
      text.startsWith("xacro include:")
    );
  });
  const firstMissing = missingFiles[0];
  const cleanFirstMissing = String(firstMissing).replace(REGISTERED_ASSET_LABEL_SUFFIX, "");
  const title =
    missingFiles.length === 1
      ? `Missing: ${cleanFirstMissing.replace(/^(mesh|texture|hfield|xacro include):\s*/i, "")}`
      : `Missing ${missingFiles.length} upload dependencies`;

  return (
    <>
      <IssueHeader
        issue={issue}
        title={title}
        description="This XML asks for files that were not uploaded. The import is paused until those files are added."
      />
      {hasRegisteredMeshes ? (
        <p className="-mt-1 mb-3 text-sm text-gray-300">
          {canUseRegisteredMeshes
            ? "Matching meshes were detected in the asset store. Upload files to replace them, or continue with the stored meshes."
            : "Some missing meshes were detected in the asset store, but the remaining dependencies still need to be uploaded."}
        </p>
      ) : null}
      <ScrollableContainer
        className="max-h-64 rounded-md border border-gray-600/50 bg-black/20 p-2"
        style={{ height: "auto" }}
      >
        <div className="space-y-3 pr-1">
          <MissingGroup title="XML includes" files={groups.include} />
          <MissingGroup title="Xacro includes" files={groups.xacroInclude} />
          <MissingGroup title="Meshes" files={groups.mesh} />
          <MissingGroup title="Textures" files={groups.texture} />
          <MissingGroup title="Height fields" files={groups.hfield} />
          <MissingGroup title="Other files" files={groups.other} />
        </div>
      </ScrollableContainer>
      <input
        ref={inputRef}
        type="file"
        aria-label="Upload missing dependency files"
        multiple
        accept={ACCEPTED_DEPENDENCY_FILES}
        onChange={actions.handleFiles}
        className="hidden"
        disabled={uploadingFiles}
      />
      <Button
        className="mt-4 w-full gap-2"
        disabled={uploadingFiles}
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="h-4 w-4" />
        {uploadingFiles ? "Uploading..." : "Upload Missing Files"}
      </Button>
      {canUseRegisteredMeshes ? (
        <Button
          variant="secondary"
          className="mt-2 w-full gap-2"
          disabled={uploadingFiles}
          onClick={() => void actions.proceedWithRegisteredMissingAssets()}
        >
          <CheckCircle2 className="h-4 w-4" />
          Use Existing Meshes
        </Button>
      ) : null}
      {canProceedWithoutMissingAssets ? (
        <Button
          variant="secondary"
          className="mt-2 w-full gap-2"
          disabled={uploadingFiles}
          onClick={() => void actions.proceedWithoutMissingAssets()}
        >
          <EyeOff className="h-4 w-4" />
          Continue Without Missing Files
        </Button>
      ) : null}
      <Button
        variant="danger"
        className="mt-2 w-full gap-2 border border-red-500/70 bg-red-600/90 text-white hover:border-red-400 hover:bg-red-500"
        disabled={uploadingFiles}
        onClick={actions.cancelMissingUploadDependencies}
      >
        Cancel
      </Button>
    </>
  );
}

function MjcfWarningsIssue({ issue, roles, setRoles }) {
  const pending = issue.detail.pending;
  const actions = useFileStore.getState().actions;
  const hasOnlyRawMeshUnitWarnings = pending.warnings.every((warning) =>
    String(warning || "").startsWith(RAW_MESH_UNIT_WARNING_PREFIX)
  );
  const hasOnlyAmbiguousGeomRoleWarnings = pending.warnings.every((warning) =>
    [AMBIGUOUS_GEOM_ROLE_WARNING_PREFIX, AMBIGUOUS_RAW_MESH_ROLE_WARNING_PREFIX].some((prefix) =>
      String(warning || "").startsWith(prefix)
    )
  );
  const ambiguousItems = hasOnlyAmbiguousGeomRoleWarnings
    ? pending.warnings.map((warning, index) => ({
        index,
        warning,
        ...parseAmbiguousRoleWarning(warning),
        role: roles[index] || "collision",
      }))
    : [];
  const hasRawMeshRoleWarnings = pending.warnings.some((warning) =>
    String(warning || "").startsWith(AMBIGUOUS_RAW_MESH_ROLE_WARNING_PREFIX)
  );
  const title = hasOnlyRawMeshUnitWarnings
    ? "Import Meshes as Millimeters?"
    : hasOnlyAmbiguousGeomRoleWarnings
      ? "Are these Visuals or Collisions?"
      : "Import has structural warnings";
  const description = hasOnlyRawMeshUnitWarnings
    ? "Some standalone meshes are unusually large when interpreted as meters. CAD exports often use millimeters."
    : hasOnlyAmbiguousGeomRoleWarnings
      ? hasRawMeshRoleWarnings
        ? `We detected ambiguity because ${pending.fileName} has standalone mesh files whose names suggest they may be collision geometry.`
        : "Ambiguous meshes detected."
      : `${pending.fileName} can still load, but some imported controls or contact settings may not work.`;
  const primaryActionLabel = hasOnlyRawMeshUnitWarnings
    ? "Import as Millimeters"
    : hasOnlyAmbiguousGeomRoleWarnings
      ? "Apply selections"
      : "Load Anyway";
  const setRole = (index, role) => setRoles((current) => ({ ...current, [index]: role }));
  const continueWithRoles = () =>
    actions.continuePendingMjcfImportWithWarnings({
      ambiguousGeomRoles: roles,
    });
  const continueAsMillimeters = () =>
    actions.continuePendingMjcfImportWithWarnings({
      rawMeshUnitScale: "millimeters",
    });
  const continueAsMeters = () =>
    actions.continuePendingMjcfImportWithWarnings({
      rawMeshUnitScale: "meters",
    });

  return (
    <>
      <IssueHeader issue={issue} title={title} description={description} tone="info" />
      {hasOnlyAmbiguousGeomRoleWarnings ? (
        <>
          <div className="-mt-1 mb-3">
            <p className="text-sm text-gray-300">
              Choose a role below, or skip to keep them as collisions for now. You can edit them
              later in the properties panel.
            </p>
          </div>
          <ScrollableContainer
            className="max-h-72 rounded-md border border-gray-600/50 bg-black/20 p-2"
            style={{ height: "auto" }}
          >
            <div className="space-y-2 pr-1">
              {ambiguousItems.map((item) => (
                <div
                  key={`${item.warning}-${item.index}`}
                  className="rounded border border-[rgba(95,95,95,0.4)] bg-[rgba(48,48,48,0.92)] px-2 py-2"
                >
                  <div className="flex min-w-0 items-start justify-between gap-3">
                    <div className="min-w-0">
                      <Tooltip content={item.geomName} fullWidth>
                        <div className="truncate text-xs font-medium text-neutral-100">
                          {item.geomName}
                        </div>
                      </Tooltip>
                      <Tooltip content={`${item.bodyName} / ${item.meshName}`} fullWidth>
                        <div className="mt-0.5 truncate text-[11px] text-neutral-400">
                          {item.bodyName} / {item.meshName}
                        </div>
                      </Tooltip>
                    </div>
                    <div className="inline-flex shrink-0 rounded-md border border-gray-700 bg-gray-900/20 p-0.5">
                      {[
                        ["visual", "Visual"],
                        ["both", "Both"],
                        ["collision", "Collision"],
                      ].map(([role, label]) => (
                        <button
                          key={role}
                          type="button"
                          className={roleButtonClass(item.role === role)}
                          onClick={() => setRole(item.index, role)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollableContainer>
        </>
      ) : (
        <ScrollableContainer
          className="max-h-64 rounded-md border border-gray-600/50 bg-black/20 p-2"
          style={{ height: "auto" }}
        >
          <div className="space-y-1 pr-1">
            {pending.warnings.map((warning, index) => (
              <div
                key={`${warning}-${index}`}
                className="rounded border border-[rgba(95,95,95,0.4)] bg-[rgba(48,48,48,0.92)] px-2 py-1 text-xs text-neutral-200"
              >
                {warning}
              </div>
            ))}
          </div>
        </ScrollableContainer>
      )}
      <Button
        className="mt-4 w-full gap-2"
        onClick={() =>
          void (hasOnlyRawMeshUnitWarnings ? continueAsMillimeters() : continueWithRoles())
        }
      >
        {primaryActionLabel}
      </Button>
      {hasOnlyRawMeshUnitWarnings ? (
        <Button
          variant="secondary"
          className="mt-2 w-full gap-2"
          onClick={() => void continueAsMeters()}
        >
          Keep Meters
        </Button>
      ) : hasOnlyAmbiguousGeomRoleWarnings ? (
        <Button
          variant="secondary"
          className="mt-2 w-full gap-2"
          onClick={() => void actions.continuePendingMjcfImportWithWarnings()}
        >
          Skip
        </Button>
      ) : null}
      <Button
        variant="danger"
        className="mt-2 w-full gap-2 border border-red-500/70 bg-red-600/90 text-white hover:border-red-400 hover:bg-red-500"
        onClick={actions.cancelPendingMjcfImportWithWarnings}
      >
        {hasOnlyAmbiguousGeomRoleWarnings || hasOnlyRawMeshUnitWarnings
          ? "Cancel upload"
          : "Cancel Import"}
      </Button>
    </>
  );
}

function ProjectIntegrityIssue({ issue, onClose }) {
  const missingSha256Count = Number(issue.detail.missingSha256Count || 0);
  const handleResaveProject = () => {
    onClose();
    void requestProjectSave();
  };

  return (
    <>
      <IssueHeader
        issue={issue}
        title="Re-save required before upload"
        description="This project can be edited locally, but cloud upload and training require updated asset checks."
        onClose={onClose}
        closeLabel="Close project integrity warning"
      />
      <div className="mt-3 rounded-md border border-gray-600/50 bg-black/20 p-3 text-sm text-gray-300">
        <div>Save the project again in this app, then upload the new .critter file.</div>
        <div className="mt-2 flex gap-2 text-xs panel-text-helper">
          <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          {missingSha256Count} assets need updated integrity data.
        </div>
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        <Button className="w-full gap-2" onClick={handleResaveProject}>
          <Save className="h-4 w-4" aria-hidden />
          Re-save Project
        </Button>
        <Button variant="secondary" className="w-full gap-2" onClick={onClose}>
          Close
        </Button>
      </div>
    </>
  );
}

function PendingPlannedIssue() {
  return (
    <div className="flex min-h-48 flex-col items-center justify-center gap-3 text-center">
      <div className="h-8 w-8 rounded-full border-2 border-sky-300 border-t-transparent animate-spin" />
      <div>
        <h2 id="upload-issue-flow-title" className="text-base font-semibold text-gray-50">
          Checking next upload issue
        </h2>
        <p className="mt-1 text-sm text-gray-300">
          The import is applying your last choice and preparing the next step.
        </p>
      </div>
    </div>
  );
}

export default function UploadIssueFlowModal() {
  const missingFiles = useFileStore((state) => state.missingFiles);
  const uploadingFiles = useFileStore((state) => state.uploadingFiles);
  const parsingXml = useFileStore((state) => state.parsingXml);
  const processingMeshes = useFileStore((state) => state.processingMeshes);
  const pendingMjcfImportWarnings = useFileStore((state) => state.pendingMjcfImportWarnings);
  const [importLimitIssue, setImportLimitIssue] = React.useState(null);
  const [projectIntegrityIssue, setProjectIntegrityIssue] = React.useState(null);
  const [sceneReplacementPending, setSceneReplacementPending] = React.useState(null);
  const [savingReplacement, setSavingReplacement] = React.useState(false);
  const [replacementSaveError, setReplacementSaveError] = React.useState("");
  const [roles, setRoles] = React.useState({});
  const [plannedIssueKeys, setPlannedIssueKeys] = React.useState([]);
  const [resolvedIssueCount, setResolvedIssueCount] = React.useState(0);
  const sceneReplacementRef = React.useRef(null);
  const sceneReplacementQueueRef = React.useRef([]);
  const previousActiveIssueKeyRef = React.useRef(null);
  const resetFlowTimerRef = React.useRef(null);
  const dialogRef = React.useRef(null);
  const pendingMjcfImportWarningKey = pendingMjcfImportWarnings?.warnings?.join("\n");

  const setCurrentSceneReplacement = React.useCallback((request) => {
    sceneReplacementRef.current = request;
    setSceneReplacementPending(request);
    setSavingReplacement(false);
    setReplacementSaveError("");
  }, []);

  React.useLayoutEffect(() => {
    const onImportLimit = (event) => {
      setImportLimitIssue(event.detail || {});
    };
    const onProjectIntegrity = (event) => {
      setProjectIntegrityIssue(event.detail || {});
    };
    const onSceneReplacement = (event) => {
      const detail = event.detail;
      if (!detail || typeof detail.resolve !== "function") return;
      detail.handled = true;
      const request = {
        title: detail.title || "Unsaved Changes",
        message: detail.message || "Save before replacing this scene?",
        confirmLabel: detail.confirmLabel || "Don't Save",
        cancelLabel: detail.cancelLabel || "Cancel",
        resolve: detail.resolve,
      };
      if (sceneReplacementRef.current) {
        sceneReplacementQueueRef.current.push(request);
        return;
      }
      setCurrentSceneReplacement(request);
    };
    const onUploadIssueFlowPlan = (event) => {
      const issueTypes = Array.isArray(event.detail?.issueTypes) ? event.detail.issueTypes : [];
      setPlannedIssueKeys(
        issueTypes.map((issueType) => String(issueType || "").trim()).filter(Boolean)
      );
      setResolvedIssueCount(0);
      previousActiveIssueKeyRef.current = null;
    };
    window.addEventListener("critter-import-limit", onImportLimit);
    window.addEventListener("critter-project-integrity", onProjectIntegrity);
    window.addEventListener(SCENE_REPLACEMENT_CONFIRM_EVENT, onSceneReplacement);
    window.addEventListener(UPLOAD_ISSUE_FLOW_PLAN_EVENT, onUploadIssueFlowPlan);
    return () => {
      window.removeEventListener("critter-import-limit", onImportLimit);
      window.removeEventListener("critter-project-integrity", onProjectIntegrity);
      window.removeEventListener(SCENE_REPLACEMENT_CONFIRM_EVENT, onSceneReplacement);
      window.removeEventListener(UPLOAD_ISSUE_FLOW_PLAN_EVENT, onUploadIssueFlowPlan);
    };
  }, [setCurrentSceneReplacement]);

  React.useLayoutEffect(() => {
    setRoles({});
  }, [pendingMjcfImportWarningKey]);

  const finishSceneReplacement = React.useCallback(
    (confirmed) => {
      const current = sceneReplacementRef.current;
      if (!current) return;
      current.resolve(Boolean(confirmed));
      const next = sceneReplacementQueueRef.current.shift() || null;
      setCurrentSceneReplacement(next);
    },
    [setCurrentSceneReplacement]
  );

  const saveBeforeSceneReplacement = React.useCallback(async () => {
    if (!sceneReplacementRef.current || savingReplacement) return;
    setSavingReplacement(true);
    setReplacementSaveError("");
    const saveActions = getProjectSaveActions();
    try {
      saveActions.markProjectSaving("local-download");
      const saved = await requestProjectSave();
      if (!saved) return;
      finishSceneReplacement(true);
    } catch (error) {
      saveActions.markProjectSaveError(error);
      setReplacementSaveError(
        error instanceof Error
          ? error.message
          : "Project could not be saved. Check the console for details."
      );
    } finally {
      setSavingReplacement(false);
    }
  }, [finishSceneReplacement, savingReplacement]);

  const issues = React.useMemo(() => {
    const active = [];
    if (importLimitIssue) {
      active.push({
        key: "importLimit",
        type: "importLimit",
        priority: ISSUE_PRIORITY.importLimit,
        detail: importLimitIssue,
      });
    }
    if (sceneReplacementPending) {
      active.push({
        key: "sceneReplacement",
        type: "sceneReplacement",
        priority: ISSUE_PRIORITY.sceneReplacement,
        detail: sceneReplacementPending,
      });
    }
    if (Array.isArray(missingFiles) && missingFiles.length > 0) {
      active.push({
        key: "missingDependencies",
        type: "missingDependencies",
        priority: ISSUE_PRIORITY.missingDependencies,
        detail: { missingFiles, uploadingFiles },
      });
    }
    if (
      pendingMjcfImportWarnings &&
      Array.isArray(pendingMjcfImportWarnings.warnings) &&
      pendingMjcfImportWarnings.warnings.length > 0
    ) {
      active.push({
        key: "mjcfWarnings",
        type: "mjcfWarnings",
        priority: ISSUE_PRIORITY.mjcfWarnings,
        detail: { pending: pendingMjcfImportWarnings },
      });
    }
    if (projectIntegrityIssue) {
      active.push({
        key: "projectIntegrity",
        type: "projectIntegrity",
        priority: ISSUE_PRIORITY.projectIntegrity,
        detail: projectIntegrityIssue,
      });
    }
    return active.sort((a, b) => a.priority - b.priority);
  }, [
    importLimitIssue,
    missingFiles,
    pendingMjcfImportWarnings,
    projectIntegrityIssue,
    sceneReplacementPending,
    uploadingFiles,
  ]);

  const activeIssueBase = issues[0] || null;
  React.useLayoutEffect(() => {
    const clearResetTimer = () => {
      if (!resetFlowTimerRef.current) return;
      window.clearTimeout(resetFlowTimerRef.current);
      resetFlowTimerRef.current = null;
    };
    const completePreviousIssueIfNeeded = () => {
      const previousKey = previousActiveIssueKeyRef.current;
      if (!previousKey) return;
      const previousStillPending = issues.some((issue) => issue.key === previousKey);
      if (!previousStillPending) {
        setResolvedIssueCount((count) => count + 1);
      }
      previousActiveIssueKeyRef.current = null;
    };

    if (!activeIssueBase) {
      completePreviousIssueIfNeeded();
      if (uploadingFiles || parsingXml || processingMeshes) return clearResetTimer;
      clearResetTimer();
      resetFlowTimerRef.current = window.setTimeout(() => {
        previousActiveIssueKeyRef.current = null;
        setPlannedIssueKeys([]);
        setResolvedIssueCount(0);
        resetFlowTimerRef.current = null;
      }, 1500);
      return clearResetTimer;
    }
    clearResetTimer();
    const previousKey = previousActiveIssueKeyRef.current;
    if (previousKey && previousKey !== activeIssueBase.key) {
      const previousStillPending = issues.some((issue) => issue.key === previousKey);
      if (!previousStillPending) {
        setResolvedIssueCount((count) => count + 1);
      }
    }
    previousActiveIssueKeyRef.current = activeIssueBase.key;
    return clearResetTimer;
  }, [activeIssueBase, issues, parsingXml, processingMeshes, uploadingFiles]);

  const activeIssue = activeIssueBase
    ? (() => {
        const plannedIndex = plannedIssueKeys.indexOf(activeIssueBase.key);
        const dynamicIndex =
          resolvedIssueCount + issues.findIndex((issue) => issue.key === activeIssueBase.key);
        const index = plannedIndex >= 0 ? plannedIndex : dynamicIndex;
        const dynamicTotal = resolvedIssueCount + issues.length;
        const plannedTotal = plannedIssueKeys.length;
        return {
          ...activeIssueBase,
          index,
          total: Math.max(index + 1, dynamicTotal, plannedTotal),
        };
      })()
    : null;
  const pendingPlannedIssue = !activeIssueBase
    ? (() => {
        if (plannedIssueKeys.length === 0) return null;
        if (
          !previousActiveIssueKeyRef.current &&
          !(uploadingFiles || parsingXml || processingMeshes)
        ) {
          return null;
        }
        const previousIndex = plannedIssueKeys.indexOf(previousActiveIssueKeyRef.current);
        const index =
          previousIndex >= 0
            ? Math.min(previousIndex + 1, plannedIssueKeys.length - 1)
            : Math.min(resolvedIssueCount, plannedIssueKeys.length - 1);
        const type = plannedIssueKeys[index];
        if (!type) return null;
        return {
          key: `pending-${type}`,
          type,
          priority: ISSUE_PRIORITY[type] || 999,
          detail: {},
          index,
          total: plannedIssueKeys.length,
          pending: true,
        };
      })()
    : null;
  const displayIssue = activeIssue || pendingPlannedIssue;
  useFocusTrap(dialogRef, Boolean(displayIssue));

  React.useEffect(() => {
    if (!displayIssue || displayIssue.type !== "sceneReplacement" || displayIssue.pending) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") finishSceneReplacement(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [displayIssue, finishSceneReplacement]);

  if (!displayIssue) return null;

  const closeOnBackdrop = () => {
    if (displayIssue.pending) return;
    const activeIssue = displayIssue;
    if (activeIssue.type === "importLimit") {
      activeIssue.detail.onCancelImport?.();
      setImportLimitIssue(null);
    } else if (activeIssue.type === "projectIntegrity") {
      setProjectIntegrityIssue(null);
    } else if (activeIssue.type === "sceneReplacement") {
      finishSceneReplacement(false);
    } else if (activeIssue.type === "missingDependencies" && !activeIssue.detail.uploadingFiles) {
      useFileStore.getState().actions.cancelMissingUploadDependencies();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closeOnBackdrop();
      }}
    >
      <div
        ref={dialogRef}
        className="flex w-full max-w-lg flex-col overflow-hidden rounded-lg border border-gray-700 text-gray-100 shadow-xl backdrop-blur-sm"
        style={{ backgroundColor: "var(--panel-surface)" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-issue-flow-title"
        tabIndex={-1}
      >
        <FlowProgressHeader issue={displayIssue} />
        <div className="min-h-0 flex-1 overflow-y-auto p-4" style={{ height: "min(70vh, 560px)" }}>
          <div key={`${displayIssue.key}-${displayIssue.index}`} className="upload-issue-flow-page">
            {displayIssue.pending ? <PendingPlannedIssue /> : null}
            {!displayIssue.pending && displayIssue.type === "importLimit" ? (
              <ImportLimitIssue issue={displayIssue} onClose={() => setImportLimitIssue(null)} />
            ) : null}
            {!displayIssue.pending && displayIssue.type === "sceneReplacement" ? (
              <SceneReplacementIssue
                issue={displayIssue}
                saving={savingReplacement}
                saveError={replacementSaveError}
                onSave={saveBeforeSceneReplacement}
                onFinish={finishSceneReplacement}
              />
            ) : null}
            {!displayIssue.pending && displayIssue.type === "missingDependencies" ? (
              <MissingDependenciesIssue issue={displayIssue} />
            ) : null}
            {!displayIssue.pending && displayIssue.type === "mjcfWarnings" ? (
              <MjcfWarningsIssue issue={displayIssue} roles={roles} setRoles={setRoles} />
            ) : null}
            {!displayIssue.pending && displayIssue.type === "projectIntegrity" ? (
              <ProjectIntegrityIssue
                issue={displayIssue}
                onClose={() => setProjectIntegrityIssue(null)}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
